import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, map, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { StateCleanupService } from './state-cleanup.service';

export interface CreditPurchase {
  id: string;
  eur_amount: number;
  credits_granted: number;
  credits_remaining: number;
  tokens_remaining: number;
  purchase_source: 'manual' | 'auto_recharge' | 'admin_grant';
  purchased_at: string | null;
  expires_at: string | null;
}

export interface CreditBalance {
  /** -1 means the plan grants unlimited tokens. */
  plan_token_quota_limit: number;
  plan_remaining_token_quota: number;
  credits_remaining: number;
  credit_tokens_remaining: number;
  combined_remaining_tokens: number;
  combined_token_quota_limit: number;
  credits_oldest_expires_at: string | null;
  purchases: CreditPurchase[];
  /** Conversion constants — the buy dialog previews EUR→tokens from these
   *  client-side, so there is no request per keystroke. */
  credits_per_eur: number;
  tokens_per_credit: number;
  min_purchase_eur: number;
  max_purchase_eur: number;
  can_buy: boolean;
  /** True once any purchase saved a card — auto-recharge can then be enabled
   *  directly; otherwise setup routes through Stripe first. */
  has_saved_card: boolean;
  /** 'requires_ug_migration' | 'no_stripe_customer' | null */
  cannot_buy_reason: string | null;
  /** Which book blocks the client: 'tokens' or 'credits' (platform switch). */
  quota_basis?: 'tokens' | 'credits';
  /** Share of the credit allowance used (0–100) on the credit basis; null =
   *  no credit limit. */
  credit_percent_used?: number | null;
}

/** One billable action and what it costs, straight from `credit_activity_costs`. */
export interface CreditPricingActivity {
  activity_type: string;
  display_name: string;
  /** Authoritative price. 1 Credit = 1000 milli — the proactive impulses cost
   *  250 milli (0.25 Cr), which a rounded credit value would show as "0". */
  credits_milli: number;
}

export interface CreditPricing {
  credits_per_eur: number;
  activities: CreditPricingActivity[];
  /** Monthly plan allowance in CREDITS. `included_credits` is null when the
   *  plan has no credit budget configured. */
  plan: {
    /** Starting point for the calculator's learner slider (migration 040).
     *  Guidance only — there is no seat limit in the data model. */
    guidance_user_count: number | null;
    included_credits: number | null;
    used_credits: number;
    remaining_credits: number | null;
  };
}

/** What we already know about a customer, to seed the calculator's three
 *  cases (`GET /credits/estimate-basis`, al-user-management). Every number is optional:
 *  null = unknown, and the calculator falls back to its own defaults. */
export interface CreditEstimateBasis {
  /** Where learners and courses come from: the LMS connection, if any. */
  source: 'moodle' | 'ilias' | 'lti' | null;
  /** Learners known from the LMS (enrolled in connected courses). */
  learners_known: number | null;
  /** Learners who used an agent in the last 30 days. */
  learners_active_30d: number | null;
  courses: {
    /** Courses kept as agent knowledge (LMS-synced, ILIAS, portal courses). */
    count: number;
    /** Courses that exist in the LMS, connected or not. */
    known: number | null;
    units_per_course: number | null;
    media_per_course: number | null;
    video_minutes_per_course: number | null;
  } | null;
  /** The customer's agents, most used first; `share` = part of the use in
   *  the last 30 days (evenly split when there was none). Sums to 1. */
  agents: { name: string; provider: string | null; share: number }[];
  usage_30d: {
    questions_per_active_learner: number | null;
    exercises_per_active_learner: number | null;
  } | null;
}

/** Optional auto-recharge setup carried on a checkout — the webhook enables
 *  auto-recharge with these once the card is saved. */
export interface AutoRechargeCheckoutSetup {
  enable_auto_recharge: true;
  auto_recharge_threshold_eur: number;
  auto_recharge_amount_eur: number;
}

export interface CreditSettings {
  auto_recharge_enabled: boolean;
  /** EUR value of the credit balance at which auto-recharge fires. */
  auto_recharge_threshold_eur: number;
  auto_recharge_threshold_eur_is_default: boolean;
  auto_recharge_amount_eur: number;
  auto_recharge_amount_eur_is_default: boolean;
  /** null => no monthly limit (opt-in). */
  auto_recharge_monthly_cap_eur: number | null;
  auto_recharge_monthly_cap_enabled: boolean;
  /** Non-null => auto-recharge is paused until the client acknowledges:
   *  'card_declined' | 'authentication_required' | 'no_payment_method' */
  auto_recharge_paused_reason: string | null;
  last_auto_recharge_at: string | null;
  last_auto_recharge_failed_at: string | null;
  last_auto_recharge_failure_code: string | null;
  alert_thresholds: number[];
  alert_thresholds_is_default: boolean;
  min_purchase_eur: number;
  max_purchase_eur: number;
}

export interface CreditQuote {
  eur_amount: number;
  credits: number;
  tokens: number;
  credits_per_eur: number;
  tokens_per_credit: number;
}

@Injectable({ providedIn: 'root' })
export class CreditService {
  balance$ = new BehaviorSubject<CreditBalance | undefined>(undefined);
  settings$ = new BehaviorSubject<CreditSettings | undefined>(undefined);

  constructor(
    protected http: HttpClient,
    protected stateCleanupService: StateCleanupService
  ) {
    this.stateCleanupService.register(() => this.resetState());
  }

  resetState() {
    this.balance$.next(undefined);
    this.settings$.next(undefined);
  }

  private get testModeQuery(): string {
    return environment.production ? '' : '?test_mode=true';
  }

  getBalance() {
    return this.http
      .get<{ data: CreditBalance }>(`${environment.userBackend}/credits/balance`)
      .pipe(
        map((res) => res.data),
        tap((balance) => this.balance$.next(balance))
      );
  }

  /** Activity price list + plan credit budget for the credit-info dialog. */
  getPricing() {
    return this.http
      .get<{ data: CreditPricing }>(`${environment.userBackend}/credits/pricing`)
      .pipe(map((res) => res.data));
  }

  /** Known learners, courses, agents and recent use — seeds the calculator. */
  getEstimateBasis() {
    return this.http
      .get<{ data: CreditEstimateBasis }>(`${environment.userBackend}/credits/estimate-basis`)
      .pipe(map((res) => res.data));
  }

  getSettings() {
    return this.http
      .get<{ data: CreditSettings }>(`${environment.userBackend}/credits/settings`)
      .pipe(
        map((res) => res.data),
        tap((settings) => this.settings$.next(settings))
      );
  }

  /** Send null for a field to reset it to the global admin default. */
  updateSettings(payload: Partial<Record<keyof CreditSettings, unknown>>) {
    return this.http
      .put<{ data: CreditSettings }>(`${environment.userBackend}/credits/settings`, payload)
      .pipe(
        map((res) => res.data),
        tap((settings) => this.settings$.next(settings))
      );
  }

  /** Clears a paused auto-recharge after the payment method was fixed. */
  acknowledgeAutoRechargeFailure() {
    return this.http
      .post<{ data: CreditSettings }>(
        `${environment.userBackend}/credits/auto-recharge/acknowledge`,
        null
      )
      .pipe(
        map((res) => res.data),
        tap((settings) => this.settings$.next(settings))
      );
  }

  /** Server-authoritative preview for the confirm step. */
  getQuote(eur: number) {
    return this.http
      .get<{ data: CreditQuote }>(`${environment.userBackend}/credits/quote?eur=${eur}`)
      .pipe(map((res) => res.data));
  }

  /** Returns a Stripe Checkout URL; the caller navigates to it. Pass `setup` to
   *  make this checkout also configure + enable auto-recharge on success. */
  createCheckout(eurAmount: number, setup?: AutoRechargeCheckoutSetup) {
    return this.http
      .post<{ data: { payment_link: string } }>(
        `${environment.userBackend}/credits/checkout${this.testModeQuery}`,
        { eur_amount: eurAmount, ...(setup ?? {}) }
      )
      .pipe(map((res) => res.data));
  }
}
